"use client";

import { Truck, MapPin, MessageCircle, Music } from "lucide-react";

interface AdvantageCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const advantages: AdvantageCardProps[] = [
  {
    icon: <Truck className="w-6 h-6" />,
    title: "전국 택배 거래",
    description: "어디서든 안전하게 배송받으세요",
  },
  {
    icon: <MapPin className="w-6 h-6" />,
    title: "근처 직거래",
    description: "가까운 곳에서 직접 만나 거래하세요",
  },
  {
    icon: <MessageCircle className="w-6 h-6" />,
    title: "즉시 채팅 소통",
    description: "바로 채팅으로 빠르게 소통하세요",
  },
  {
    icon: <Music className="w-6 h-6" />,
    title: "전 분야 악기",
    description: "클래식·실용·음향·국악 모든 악기를 거래하세요",
  },
];

export default function AdvantagesSection() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#161a2c] via-[#1f2850] to-[#2c3a7b] text-white">
      {/* 미니멀 배경 장식 */}
      <div className="absolute -top-32 -left-24 w-72 h-72 bg-blue-500/15 blur-3xl rounded-full" />
      <div className="absolute bottom-0 right-[-10rem] w-[28rem] h-[28rem] bg-indigo-500/20 blur-[140px] rounded-full" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-24 py-16 sm:py-20 lg:py-24">
        {/* 섹션 헤더 */}
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-[-0.02em] mb-4">
            <span className="bg-gradient-to-r from-white via-blue-100 to-indigo-100 bg-clip-text text-transparent">
              왜 여기서 거래하나요?
            </span>
          </h2>
        </div>

        {/* 장점 카드 그리드 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {advantages.map((advantage, index) => (
            <AdvantageCard key={index} {...advantage} />
          ))}
        </div>
      </div>
    </section>
  );
}

function AdvantageCard({ icon, title, description }: AdvantageCardProps) {
  return (
    <div className="group relative rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6 sm:p-8 hover:bg-white/10 hover:border-white/20 transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl hover:shadow-blue-500/10">
      {/* 카드 배경 그라데이션 (hover 시) */}
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500/0 to-indigo-500/0 group-hover:from-blue-500/10 group-hover:to-indigo-500/10 transition-all duration-300 opacity-0 group-hover:opacity-100" />

      {/* 콘텐츠 */}
      <div className="relative flex flex-col items-center text-center space-y-4">
        {/* 아이콘 */}
        <div className="flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-white/10 group-hover:bg-white/15 text-white transition-all duration-300 transform group-hover:scale-110">
          {icon}
        </div>

        {/* 타이틀 */}
        <h3 className="text-lg sm:text-xl font-semibold text-white/90 group-hover:text-white transition-colors tracking-tight">
          {title}
        </h3>

        {/* 설명 */}
        <p className="text-sm sm:text-base text-white/70 group-hover:text-white/80 transition-colors leading-relaxed">
          {description}
        </p>
      </div>
    </div>
  );
}

